import { useState } from "react";
import { ShieldCheck } from "lucide-react";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export function LeadCapture({ profileKey, intensity, screenTime }) {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [hp, setHp] = useState("");
  const [status, setStatus] = useState("idle");
  const [serverError, setServerError] = useState("");

  const emailValid = EMAIL_RE.test(email.trim());
  const canSubmit = emailValid && consent && status !== "sending";

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setServerError("");
    setStatus("sending");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          profile: profileKey,
          intensity,
          screen_time_range: screenTime || "Nu știu",
          consent: true,
          hp,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(typeof data.detail === "string" ? data.detail : "");
      }
      setStatus("sent");
    } catch (err) {
      setStatus("error");
      setServerError(
        err.message && !err.message.startsWith("Unexpected")
          ? err.message
          : "Ceva n-a mers bine. Încearcă din nou în câteva minute.",
      );
    }
  };

  return (
    <section
      aria-labelledby="lead-heading"
      data-testid="lead-card"
      className="mt-12 overflow-hidden rounded-[28px] border border-teal/25 bg-raised"
    >
      <div aria-hidden="true" className="h-1 w-full bg-gradient-to-r from-teal via-tealbright to-blue" />
      <div className="p-7 sm:p-9">
      <p className="text-[12px] font-semibold uppercase tracking-[0.2em] text-soft">
        Vrei să mergi mai departe?
      </p>
      <h2
        id="lead-heading"
        data-testid="lead-title"
        className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-3xl"
      >
        <span className="bg-gradient-to-r from-teal to-blue bg-clip-text text-transparent">
          RECÂȘTIGĂ CONTROLUL
        </span>
      </h2>
      <p className="mt-4 leading-relaxed text-soft">
        7 pași practici pentru a ieși din scrollul automat și a folosi telefonul cu mai multă
        intenție.
      </p>
      <p className="mt-1.5 text-sm font-medium text-ink/90">Primești gratuit ghidul MindRegen.</p>

      {status === "sent" ? (
        <p data-testid="lead-success" role="status" className="mt-7 rounded-2xl border border-teal/30 bg-teal/10 p-5 font-medium leading-relaxed text-teal">
          Verifică-ți emailul. Ți-am trimis un mesaj de confirmare; apasă linkul din el și primești
          ghidul. Uită-te și în Spam/Promoții.
        </p>
      ) : (
        <form onSubmit={submit} noValidate className="mt-6">
          <div aria-hidden="true" style={{ display: "none" }}>
            <label htmlFor="lead-hp">Nu completa acest câmp</label>
            <input
              id="lead-hp"
              data-testid="lead-honeypot"
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={hp}
              onChange={(e) => setHp(e.target.value)}
            />
          </div>

          <div>
            <label htmlFor="lead-email" className="block text-sm font-medium text-ink">
              Adresa ta de email
            </label>
            <input
              id="lead-email"
              data-testid="lead-email-input"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-invalid={email && !emailValid ? true : undefined}
              className="mt-2 min-h-[56px] w-full rounded-xl border border-teal/25 bg-surface px-4 text-base text-ink transition-colors duration-200 placeholder:text-soft/50 focus:border-teal"
              placeholder="nume@email.ro"
            />
            {email && !emailValid && (
              <p className="mt-2 text-[13px] text-[#FF8A7A]">Introdu o adresă de email validă.</p>
            )}
          </div>

          <div className="mt-5 flex items-start gap-3 rounded-2xl border border-white/[0.06] bg-surface/70 p-4">
            <input
              id="lead-consent"
              data-testid="lead-consent-checkbox"
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-0.5 h-5 w-5 shrink-0 accent-[#20D6C7]"
            />
            <label htmlFor="lead-consent" className="text-[13px] leading-relaxed text-soft">
              Sunt de acord să primesc ghidul MindRegen și materiale educaționale prin email. Mă pot
              dezabona oricând. Detalii în{" "}
              <a
                href="/confidentialitate"
                data-testid="lead-privacy-link"
                className="font-medium text-teal underline underline-offset-4 transition-colors duration-200 hover:text-ink"
              >
                Politica de confidențialitate
              </a>
              .
            </label>
          </div>

          <p aria-live="assertive" data-testid="lead-error" role="alert" className="empty:hidden mt-4 text-sm font-medium text-[#FF8A7A]">
            {serverError}
          </p>

          <button
            type="submit"
            data-testid="lead-submit-button"
            disabled={!canSubmit}
            className="glow-cta mt-6 w-full min-h-[56px] rounded-full bg-teal px-10 font-display text-[15px] font-bold uppercase tracking-wide text-base transition-[background-color,box-shadow] duration-200 hover:bg-teal-dark disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none"
          >
            {status === "sending" ? "Se trimite…" : "Trimite-mi ghidul →"}
          </button>
          <p className="mt-4 flex items-center justify-center gap-2 text-[13px] text-soft">
            <ShieldCheck className="h-4 w-4 text-teal" aria-hidden="true" />
            Fără spam. Te poți dezabona oricând.
          </p>
        </form>
      )}
      </div>
    </section>
  );
}
